import type { TFunction } from "react-i18next"
import { Duration } from "luxon"
import type { CalendarDurationLike } from "@oysterhr/common"
import type { CalendarDuration, CalendarDurationRange, Maybe } from "@oysterhr/schema"
import { CalendarDurationType } from "@oysterhr/schema"

export const timeWithUnit = (t: TFunction, time: CalendarDurationLike | null | undefined) => {
  if (!time) return ""
  const unitTranslation = t(`enums.timeAmounts.${time.unit}`, {
    count: time.value || 0,
    ns: "knowledge_base/HiringAdvisor",
  })
  return `${time.value} ${unitTranslation}`
}

export const concatPhrase = <T = string>(
  t: TFunction,
  arr: T[],
  translationKey: string,
  params?: Record<string, unknown>,
) => {
  if (!params) return arr
  return Object.values(params).every(v => v != null) ? [...arr, t(translationKey, params)] : arr
}

export const convertToDays = (unit: CalendarDurationType, value: number) => {
  switch (unit) {
    case CalendarDurationType.Days:
      return value
    case CalendarDurationType.Weeks:
      return value * 7
    case CalendarDurationType.Months:
      return value * 30
    case CalendarDurationType.Years:
      return value * 365
    default:
      return value
  }
}

export const maxDuration = (durations: CalendarDuration[]) => {
  if (!durations?.length) {
    return null
  }

  const max = durations?.reduce((acc, cur) => {
    if (cur.value == null || acc.value == null) return acc
    const convertedCur = convertToDays(cur.unit, cur.value)
    const convertedAcc = convertToDays(acc.unit, acc.value)
    return convertedCur > convertedAcc ? cur : acc
  }, durations[0])
  return max
}

export const minDuration = (durations: CalendarDuration[]) => {
  if (!durations?.length) {
    return null
  }

  const min = durations?.reduce((acc, cur) => {
    if (cur.value == null || acc.value == null) return acc
    const convertedCur = convertToDays(cur.unit, cur.value)
    const convertedAcc = convertToDays(acc.unit, acc.value)
    return convertedCur < convertedAcc ? cur : acc
  }, durations[0])
  return min
}

/**
 * Sorts an array by a duration property
 */
export const sortByDuration = (a: CalendarDuration, b: CalendarDuration) => {
  return convertToDays(a.unit, a.value ?? 0) - convertToDays(b.unit, b.value ?? 0)
}

type ComparisonRule = "inclusive" | "exclusive"
const compare = (a: Duration, operator: ">" | "<", b: Duration, rule: ComparisonRule) =>
  operator === ">" ? (rule === "inclusive" ? a >= b : a > b) : rule === "inclusive" ? a <= b : a < b

type RangeRules = {
  start: ComparisonRule
  end: ComparisonRule
}

export const isBetweenCalendarDurationRange = (
  value: Duration,
  range: CalendarDurationRange,
  rules?: RangeRules,
) => {
  rules = { start: "inclusive", end: "inclusive", ...rules }

  const safeMax = {
    unit: CalendarDurationType.Days,
    value: Number.MAX_SAFE_INTEGER,
  }
  const safeMin = {
    unit: CalendarDurationType.Days,
    value: 0,
  }

  const rangeDurationMin = convertCalendarDurationToDuration(range.min || safeMin)
  const rangeDurationMax = convertCalendarDurationToDuration(range.max || safeMax)

  return (
    compare(value, ">", rangeDurationMin, rules.start) &&
    compare(value, "<", rangeDurationMax, rules.end)
  )
}

export const calendarDurationTypeToLuxonDurationType = (type: CalendarDurationType) => {
  switch (type) {
    case CalendarDurationType.Years:
      return "years"
    case CalendarDurationType.Months:
      return "months"
    case CalendarDurationType.Weeks:
      return "weeks"
    case CalendarDurationType.Days:
      return "days"
    default:
      throw new Error("Unhandled duration type")
  }
}

export const convertCalendarDurationToDuration = (
  calendarDuration: Maybe<CalendarDuration> | undefined,
) => {
  const { value, unit } = calendarDuration || { value: 0, unit: CalendarDurationType.Days }

  return Duration.fromObject({
    [calendarDurationTypeToLuxonDurationType(unit)]: value,
  })
}
